import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { type ChatAction } from '@atlassian/rovo-conversation-actions/types';
import { messages } from '@atlassian/rovo-fomo-nudge/i18n';
import { createFomoNudgeAction } from '@atlassian/rovo-fomo-nudge/create-fomo-nudge-action';
import { useFomoCollaboratorsBatched } from '@atlassian/rovo-fomo-nudge/use-fomo-collaborators-batched';
import { type FomoNudgeStorage } from '@atlassian/rovo-fomo-nudge/show-once-storage';
import { useCanShowFomoNudge } from '@atlassian/rovo-fomo-nudge/use-can-show-fomo-nudge';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { confluenceLocalStorageInstance as localStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { useFomoNudgeTrait, FOMO_NUDGE_TRAIT_NAME } from './useFomoNudgeTrait.ts';

/** How long to suppress the FOMO nudge after the user clicks or dismisses it (2 weeks). */
const FOMO_NUDGE_SUPPRESSION_DURATION_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Confluence localStorage-backed FomoNudgeStorage adapter.
 *
 * Reads/writes via StorageManager (AtlBrowserStorage-aware) so the key is
 * subject to the atlassian-cookies consent registry when
 * confluence_browser_storage_controls is on.
 */
const confluenceFomoNudgeStorage: FomoNudgeStorage = {
	hasBeenShown(): boolean {
		const dismissedAt = localStorageInstance.getItem(localStorageKeys.FOMO_NUDGE_DISMISSED);
		return !!dismissedAt && Date.now() - Number(dismissedAt) < FOMO_NUDGE_SUPPRESSION_DURATION_MS;
	},
	markShown(): void {
		localStorageInstance.setItem(localStorageKeys.FOMO_NUDGE_DISMISSED, Date.now());
	},
};

/**
 * Phasing (matches the Jira side):
 *   1 — gates compile, builder returns null, no UI rendered.
 *   2 — avatar UI + i18n via shared package.
 *   3 — non-Rovo-MAU TAP trait gate.
 *   4 — collaborator data via TWG / AGG.
 *   5 — TTL-based localStorage suppression (TREX-1585). ✓ Done.
 *
 * NOTE: launch-date scheduling is handled in Statsig (per project decision),
 * not in code — this hook has no notion of "today".
 */
export function useFomoNudgeAction(): ChatAction | null {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Cookie consent check — the hook reads the `rovo_fomo_nudge_cookie_policy_check`
	// killswitch internally. When the killswitch is on, the nudge only shows for
	// users who have consented to Functional storage. When the killswitch is off,
	// canShowFromCookiePreference is `true` immediately (legacy behavior, no network/storage check).
	// Runs before any TAP / Relay fetch so non-consenting users incur no extra
	// network requests.
	const canShowFromCookiePreference =
		useCanShowFomoNudge() && fg('rovo_button_collab_teammates_fomo_nudge_enabled');

	// Read suppression once per render. Synchronous localStorage read via the
	// adapter; cost is sub-microsecond. Used to short-circuit the trait + Relay
	// fetches below so users inside the 14-day suppression window incur no
	// network requests.
	const suppressed = confluenceFomoNudgeStorage.hasBeenShown();

	// Trait fetch is NOT gated on `expVal` — we need `isRovoChatMau` before
	// the experiment can be exposed.
	const {
		aaids,
		isReady: traitReady,
		loaded: traitRan,
		isRovoChatMau,
	} = useFomoNudgeTrait({
		enabled: canShowFromCookiePreference === true && !suppressed,
	});

	// First `expVal` call site — only reached after `isRovoChatMau === false`.
	const {
		collaborators,
		isReady: collaboratorsReady,
		error: collaboratorError,
	} = useFomoCollaboratorsBatched(aaids, {
		enabled:
			canShowFromCookiePreference === true &&
			!suppressed &&
			traitReady &&
			isRovoChatMau === false &&
			aaids.length > 0 &&
			expVal('rovo_button_collab_teammates_fomo_nudge', 'isEnabled', false),
	});

	const hasReportedRef = useRef(false);

	useEffect(() => {
		if (!traitReady || !traitRan) return;
		if (isRovoChatMau !== false) return;
		if (aaids.length > 0 && !collaboratorsReady) return;
		if (!fg('rovo_button_collab_teammates_fomo_nudge_enabled')) return;
		if (!expVal('rovo_button_collab_teammates_fomo_nudge', 'isEnabled', false)) return;
		if (hasReportedRef.current) return;
		hasReportedRef.current = true;

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'fomoNudge',
				action: 'traitResolved',
				actionSubject: 'fomoNudge',
				attributes: {
					traitName: FOMO_NUDGE_TRAIT_NAME,
					aaidCount: aaids.length,
				},
			},
		}).fire();

		const zeroResolved = aaids.length > 0 && collaborators.length === 0;
		if (collaboratorError || zeroResolved) {
			let errorMessage: string;
			if (collaboratorError instanceof Error) {
				errorMessage = collaboratorError.message;
			} else if (collaboratorError) {
				errorMessage = String(collaboratorError);
			} else {
				errorMessage = 'no collaborators resolved';
			}
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'fomoNudge',
					action: 'failed',
					actionSubject: 'collaboratorResolution',
					attributes: {
						operation: 'fetchUsersByAaids',
						requestedAaidCount: aaids.length,
						errorMessage,
					},
				},
			}).fire();
			if (collaboratorError) {
				getMonitoringClient().submitError(
					collaboratorError instanceof Error
						? collaboratorError
						: new Error(String(collaboratorError)),
					{ attribution: Attribution.AI_MATE },
				);
			}
		}
	}, [
		traitReady,
		traitRan,
		collaboratorsReady,
		isRovoChatMau,
		aaids,
		collaborators,
		collaboratorError,
		createAnalyticsEvent,
	]);

	return useMemo(() => {
		// Killswitch — also checks is_rovo_chat_mau
		if (!fg('rovo_button_collab_teammates_fomo_nudge_enabled')) {
			return null;
		}

		// Cookie consent check — when the killswitch is on, only proceed if
		// the user has granted Functional storage consent. When the killswitch
		// is off, canShowFromCookiePreference is `true` immediately (no-op).
		// canShowFromCookiePreference === null → check is still pending; canShowFromCookiePreference === false → denied.
		if (canShowFromCookiePreference !== true) {
			return null;
		}

		// Check storage — if the nudge was dismissed within the suppression
		// window, bail before re-exposing the experiment (TREX-1585 requirement 4).
		if (suppressed) {
			return null;
		}

		// `traitReady` + MAU check MUST stay above the `expVal` call so
		// non-eligible users are never exposed to the experiment.
		if (!traitReady) return null;
		if (isRovoChatMau !== false) return null;

		if (!expVal('rovo_button_collab_teammates_fomo_nudge', 'isEnabled', false)) {
			return null;
		}

		// No eligible collaborators — hide the nudge.
		if (collaborators.length === 0) return null;
		// Wait for avatar/name resolution to complete — avoid flashing placeholder
		// avatars before the real data lands.
		if (!collaboratorsReady) return null;

		return createFomoNudgeAction({
			collaborators,
			title: formatMessage(messages.fomoNudgeTitle),
			description: formatMessage(messages.fomoNudgeDescription),
			onAccept: () => confluenceFomoNudgeStorage.markShown(),
			onDismiss: () => confluenceFomoNudgeStorage.markShown(),
		});
	}, [
		canShowFromCookiePreference,
		suppressed,
		traitReady,
		isRovoChatMau,
		collaboratorsReady,
		collaborators,
		formatMessage,
	]);
}
